import React from "react";
import {
    Box,
    Flex,
    Text,
    HStack,
    IconButton,
    useDisclosure,
    Collapse,
    VStack,
    Button,
    Badge,
    Icon,
    Container,
    Image
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FaFacebook, FaInstagram, FaBars, FaTimes, FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import logo from "../../assets/images/legalLogo.jpg";
import { useLanguage } from "../../context/LanguageContext";
import { LANGUAGES } from "../../translations";

const Nav = () => {
    const { isOpen, onToggle, onClose } = useDisclosure();
    const location = useLocation();
    const { currentLanguage, changeLanguage, t } = useLanguage();

    const navLinks = [
        { path: "/", label: t("nav.home") },
        { path: "/about", label: t("nav.about") },
        { path: "/services", label: t("nav.services") },
        // { path: "/works", label: "Դատական գործեր" },
        { path: "/contact", label: t("nav.contact") },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <Box
            as="header"
            position="sticky"
            top={0}
            zIndex={100}
            w="100%"
            bg="rgba(11, 19, 43, 0.94)"
            backdropFilter="blur(16px)"
            WebkitBackdropFilter="blur(16px)"
            borderBottom="1px solid rgba(255, 255, 255, 0.08)"
            boxShadow="0 10px 30px -10px rgba(0, 0, 0, 0.3)"
            transition="all 0.3s ease"
        >
            {/* Top utility bar */}
            <Box
                bg="rgba(15, 23, 42, 0.6)"
                borderBottom="1px solid rgba(255, 255, 255, 0.04)"
                py={1.5}
                display={{ base: "none", md: "block" }}
            >
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <Flex justify="space-between" align="center" fontSize="xs" color="gray.300">
                        <HStack spacing={6}>
                            <HStack as="a" href="tel:+37477531309" _hover={{ color: "brand.300" }} transition="color 0.2s">
                                <Box w={2} h={2} borderRadius="full" bg="green.400" boxShadow="0 0 8px #48bb78" />
                                <Icon as={FaPhoneAlt} color="brand.400" />
                                <Text fontWeight="medium">+374 77 53 13 09</Text>
                            </HStack>
                            <HStack as="a" href="mailto:info@legaltitan.am" _hover={{ color: "brand.300" }} transition="color 0.2s">
                                <Icon as={FaEnvelope} color="brand.400" />
                                <Text fontWeight="medium">info@legaltitan.am</Text>
                            </HStack>
                            <Text color="gray.400">{t("nav.workingHours")}</Text>
                        </HStack>

                        <HStack spacing={3}>
                            <Box
                                as="a"
                                href="https://www.facebook.com/profile.php?id=61557661694303"
                                target="_blank"
                                rel="noopener noreferrer"
                                p={1.5}
                                borderRadius="md"
                                color="gray.400"
                                _hover={{ color: "white", bg: "whiteAlpha.200", transform: "scale(1.1)" }}
                                transition="all 0.2s"
                            >
                                <FaFacebook size="14px" />
                            </Box>
                            <Box
                                as="a"
                                href="https://www.instagram.com/legal_titan_llc______/"
                                target="_blank"
                                rel="noopener noreferrer"
                                p={1.5}
                                borderRadius="md"
                                color="gray.400"
                                _hover={{ color: "white", bg: "whiteAlpha.200", transform: "scale(1.1)" }}
                                transition="all 0.2s"
                            >
                                <FaInstagram size="14px" />
                            </Box>

                            {/* Language Switcher */}
                            <HStack spacing={1} pl={3} borderLeft="1px solid rgba(255, 255, 255, 0.15)">
                                {LANGUAGES.map((lang) => (
                                    <Button
                                        key={lang.code}
                                        size="xs"
                                        variant="ghost"
                                        h="22px"
                                        px={2}
                                        borderRadius="md"
                                        fontSize="11px"
                                        fontWeight={currentLanguage === lang.code ? "800" : "600"}
                                        color={currentLanguage === lang.code ? "#dfba73" : "gray.400"}
                                        bg={currentLanguage === lang.code ? "whiteAlpha.200" : "transparent"}
                                        _hover={{ color: "white", bg: "whiteAlpha.150" }}
                                        onClick={() => changeLanguage(lang.code)}
                                        leftIcon={<Image src={lang.flag} alt={lang.label} w="13px" h="9px" borderRadius="xs" objectFit="cover" />}
                                    >
                                        {lang.label}
                                    </Button>
                                ))}
                            </HStack>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            {/* Main Navigation Bar */}
            <Container maxW="container.xl" px={{ base: 4, md: 8 }} py={{ base: 3, md: 3.5 }}>
                <Flex justify="space-between" align="center">
                    {/* Brand Logo */}
                    <RouterLink to="/" style={{ textDecoration: "none" }}>
                        <HStack spacing={3} cursor="pointer" role="group">
                            <Box
                                w={{ base: "42px", md: "48px" }}
                                h={{ base: "42px", md: "48px" }}
                                borderRadius="xl"
                                overflow="hidden"
                                bg="white"
                                p={1}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                boxShadow="0 4px 15px rgba(0, 0, 0, 0.25)"
                                border="1px solid rgba(223, 186, 115, 0.4)"
                                _groupHover={{ transform: "scale(1.05)", borderColor: "#dfba73" }}
                                transition="all 0.3s ease"
                            >
                                <Image
                                    src={logo}
                                    alt="Legal Titan Logo"
                                    maxH="100%"
                                    maxW="100%"
                                    objectFit="contain"
                                />
                            </Box>

                            <Box>
                                <HStack spacing={1.5} align="baseline">
                                    <Text
                                        fontSize={{ base: "xl", md: "2xl" }}
                                        fontWeight="800"
                                        color="white"
                                        letterSpacing="-0.03em"
                                        lineHeight="1"
                                    >
                                        Legal<Text as="span" color="#dfba73">Titan</Text>
                                    </Text>
                                    <Badge
                                        bg="whiteAlpha.200"
                                        color="gray.200"
                                        fontSize="9px"
                                        px={1.5}
                                        py={0.5}
                                        borderRadius="md"
                                        letterSpacing="0.05em"
                                        fontWeight="700"
                                    >
                                        LLC
                                    </Badge>
                                </HStack>
                                <Text
                                    fontSize="9px"
                                    color="gray.400"
                                    letterSpacing="0.1em"
                                    textTransform="uppercase"
                                    fontWeight="600"
                                    mt={0.5}
                                    display={{ base: "none", sm: "block" }}
                                >
                                    Law & Consultancy
                                </Text>
                            </Box>
                        </HStack>
                    </RouterLink>

                    {/* Desktop Menu Links */}
                    <HStack spacing={{ md: 1, lg: 2 }} display={{ base: "none", md: "flex" }}>
                        {navLinks.map(({ path, label }) => {
                            const active = isActive(path);
                            return (
                                <Box
                                    key={path}
                                    as={RouterLink}
                                    to={path}
                                    px={{ md: 3, lg: 4 }}
                                    py={2}
                                    borderRadius="lg"
                                    fontSize="sm"
                                    fontWeight={active ? "700" : "600"}
                                    color={active ? "#dfba73" : "gray.200"}
                                    bg={active ? "whiteAlpha.100" : "transparent"}
                                    position="relative"
                                    transition="all 0.2s ease"
                                    _hover={{
                                        color: "white",
                                        bg: "whiteAlpha.150",
                                        transform: "translateY(-1px)",
                                    }}
                                >
                                    {label}
                                    {active && (
                                        <Box
                                            position="absolute"
                                            bottom="2px"
                                            left="50%"
                                            transform="translateX(-50%)"
                                            w="16px"
                                            h="2px"
                                            bg="#dfba73"
                                            borderRadius="full"
                                        />
                                    )}
                                </Box>
                            );
                        })}

                        {/* Legislation Dropdown - commented out for now
                        <Menu isLazy>
                            <MenuButton
                                as={Button}
                                variant="ghost"
                                size="sm"
                                rightIcon={<FaChevronDown size="10px" />}
                                color="gray.200"
                                fontWeight="600"
                                px={{ md: 3, lg: 4 }}
                                py={2}
                                h="auto"
                                borderRadius="lg"
                                _hover={{ color: "white", bg: "whiteAlpha.150" }}
                                _active={{ bg: "whiteAlpha.200" }}
                            >
                                Օրենսդրություն
                            </MenuButton>
                            <MenuList
                                bg="#0b132b"
                                borderColor="whiteAlpha.200"
                                boxShadow="0 20px 40px rgba(0,0,0,0.5)"
                                p={2}
                                borderRadius="xl"
                                minW="220px"
                            >
                                <MenuItem
                                    as={RouterLink}
                                    to="/documents"
                                    bg="transparent"
                                    _hover={{ bg: "whiteAlpha.150", color: "#dfba73" }}
                                    borderRadius="lg"
                                    px={3}
                                    py={2.5}
                                    color="gray.200"
                                    fontWeight="600"
                                    fontSize="sm"
                                    icon={<Icon as={FaBook} color="#dfba73" />}
                                >
                                    Օրենսդրական Գրադարան
                                </MenuItem>
                            </MenuList>
                        </Menu>
                        */}
                    </HStack>

                    {/* Right side CTA & Mobile trigger */}
                    <HStack spacing={3}>
                        <Button
                            as={RouterLink}
                            to="/contact"
                            size="sm"
                            display={{ base: "none", sm: "inline-flex" }}
                            bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                            color="#0b132b"
                            fontWeight="700"
                            fontSize="xs"
                            px={4}
                            py={2}
                            borderRadius="lg"
                            boxShadow="0 4px 15px rgba(223, 186, 115, 0.3)"
                            _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "0 6px 20px rgba(223, 186, 115, 0.4)",
                            }}
                            transition="all 0.25s ease"
                        >
                            {t("nav.consultation")}
                        </Button>

                        <IconButton
                            display={{ base: "flex", md: "none" }}
                            onClick={onToggle}
                            icon={isOpen ? <FaTimes /> : <FaBars />}
                            variant="ghost"
                            aria-label="Toggle navigation"
                            color="white"
                            size="sm"
                            _hover={{ bg: "whiteAlpha.200" }}
                        />
                    </HStack>
                </Flex>
            </Container>

            {/* Mobile Dropdown Menu */}
            <Collapse in={isOpen} animateOpacity>
                <Box
                    bg="#0f172a"
                    borderTop="1px solid rgba(255, 255, 255, 0.08)"
                    px={4}
                    py={6}
                    display={{ base: "block", md: "none" }}
                    boxShadow="xl"
                >
                    <VStack spacing={2} align="stretch">
                        {navLinks.map(({ path, label }) => {
                            const active = isActive(path);
                            return (
                                <Box
                                    key={path}
                                    as={RouterLink}
                                    to={path}
                                    onClick={onClose}
                                    px={4}
                                    py={3}
                                    borderRadius="lg"
                                    fontWeight="600"
                                    fontSize="md"
                                    color={active ? "#dfba73" : "white"}
                                    bg={active ? "whiteAlpha.150" : "transparent"}
                                    _hover={{ bg: "whiteAlpha.100" }}
                                >
                                    {label}
                                </Box>
                            );
                        })}

                        {/*
                        <Box
                            as={RouterLink}
                            to="/documents"
                            onClick={onClose}
                            px={4}
                            py={3}
                            borderRadius="lg"
                            fontWeight="600"
                            fontSize="md"
                            color={isActive("/documents") ? "#dfba73" : "white"}
                            bg={isActive("/documents") ? "whiteAlpha.150" : "transparent"}
                            _hover={{ bg: "whiteAlpha.100" }}
                        >
                            Օրենսդրական Գրադարան
                        </Box>
                        */}

                        <Box pt={4} borderTop="1px solid rgba(255, 255, 255, 0.08)">
                            <Button
                                as={RouterLink}
                                to="/contact"
                                onClick={onClose}
                                w="100%"
                                bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                                color="#0b132b"
                                fontWeight="700"
                                py={3}
                                borderRadius="lg"
                            >
                                {t("nav.consultation")}
                            </Button>
                        </Box>

                        {/* Mobile Language Switcher */}
                        <HStack spacing={2} justify="center" pt={3} borderTop="1px solid rgba(255, 255, 255, 0.08)">
                            {LANGUAGES.map((lang) => (
                                <Button
                                    key={lang.code}
                                    size="sm"
                                    variant="ghost"
                                    px={3}
                                    borderRadius="lg"
                                    fontSize="xs"
                                    fontWeight={currentLanguage === lang.code ? "800" : "600"}
                                    color={currentLanguage === lang.code ? "#dfba73" : "gray.300"}
                                    bg={currentLanguage === lang.code ? "whiteAlpha.200" : "transparent"}
                                    onClick={() => changeLanguage(lang.code)}
                                    leftIcon={<Image src={lang.flag} alt={lang.label} w="16px" h="11px" borderRadius="xs" objectFit="cover" />}
                                >
                                    {lang.label}
                                </Button>
                            ))}
                        </HStack>

                        <HStack spacing={4} justify="center" pt={4} color="gray.400">
                            <Box as="a" href="tel:+37477531309" p={2} _hover={{ color: "white" }}>
                                <FaPhoneAlt size="16px" />
                            </Box>
                            <Box as="a" href="mailto:info@legaltitan.am" p={2} _hover={{ color: "white" }}>
                                <FaEnvelope size="16px" />
                            </Box>
                            <Box as="a" href="https://www.facebook.com/profile.php?id=61557661694303" target="_blank" rel="noopener noreferrer" p={2} _hover={{ color: "white" }}>
                                <FaFacebook size="16px" />
                            </Box>
                            <Box as="a" href="https://www.instagram.com/legal_titan_llc______/" target="_blank" rel="noopener noreferrer" p={2} _hover={{ color: "white" }}>
                                <FaInstagram size="16px" />
                            </Box>
                        </HStack>
                    </VStack>
                </Box>
            </Collapse>
        </Box>
    );
};

export default Nav;